import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';

import { Product } from '~shared/models';
import { Observable, Subject } from 'rxjs';
import { BlackDealsFeatureFlagService } from '~shared/services/black-deals-feature-flag.service';
import { PriceTypes } from '~shared/modules/price-type-toggler/models/price-types.enum';
import { VariantType, VariantTypeObject } from '~shared/models/product-card/product-variant.model';
import { AddToCardConfig } from '~shared/modules/product-card/models/add-to-card-config.model';
import { takeUntil } from 'rxjs/operators';
import { PlpAddToCartService } from '~shared/modules/products-grid/services/plp-add-to-cart.service';

@Component({
  selector: 'app-product-advanced-carousel',
  templateUrl: './product-advanced-carousel.component.html',
  styleUrls: ['./product-advanced-carousel.component.scss'],
})
export class ProductAdvancedCarouselComponent implements OnInit {
  @Input() products: Product[];
  @Input() isMobile: boolean;
  @Input() isDesktop: boolean;
  @Input() hasEndPadding: boolean;
  @Input() title?: string
  @Input() trackingName?: string

  @Output() swipeLeft = new EventEmitter();
  @Output() swipeRight = new EventEmitter();
  @Output() productClicked = new EventEmitter<{ product: Product; priceType: PriceTypes; index: number }>();
  @Output() productCarouselPrev = new EventEmitter();
  @Output() productCarouselNext = new EventEmitter();

  isBlackDealsEnabled$: Observable<boolean>;
  public addToCartConfig: AddToCardConfig;

  private destroySubject$ = new Subject<boolean>();

  constructor(
    private blackDealsFeatureFlagService: BlackDealsFeatureFlagService,
    private plpAddToCartService: PlpAddToCartService
  ) {}

  ngOnInit(): void {
    this.isBlackDealsEnabled$ = this.blackDealsFeatureFlagService.isEnabled();
    this.plpAddToCartService
      .getPlpsAddToCartVariant()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((addToCartConfig: AddToCardConfig) => {
        this.addToCartConfig = addToCartConfig;
      });
  }

  ngOnDestroy() {
    this.destroySubject$.next();
    this.destroySubject$.complete();
  }

  trackById(_, item: Product) {
    return item.id;
  }

  public someProductHasVariants(): { hasSizes: boolean; hasColors: boolean } {
    const hasColors: boolean = this.products?.some((product: Product): boolean => {
      const variantTypeObject: VariantTypeObject = product?.variant?.types;
      const variantTypes: [string, VariantType][] = variantTypeObject ? Object.entries(variantTypeObject) : [];
      const colorVariants: [string, VariantType][] = variantTypes?.filter(
        (variant: [string, VariantType]): boolean => variant[0] === 'color'
      );

      return colorVariants && !!colorVariants?.length;
    });

    const hasSizes: boolean = this.products?.some((product: Product): boolean => {
      const variantTypeObject: VariantTypeObject = product?.variant?.types;
      const variantTypes: [string, VariantType][] = variantTypeObject ? Object.entries(variantTypeObject) : [];
      const notColorVariants: [string, VariantType][] = variantTypes?.filter(
        (variant: [string, VariantType]) => variant[0] !== 'color'
      );
      return notColorVariants && !!notColorVariants?.length;
    });

    return {
      hasColors,
      hasSizes,
    };
  }

  someProductsHasTotalUnitsOrBasePrice() {
    return this.products?.some((product: Product) => {
      return product.bestOffer?.unitPrice?.unitCount > 1 || product?.bestOffer?.basePrice;
    });
  }

  onProductClick(product: Product, priceType: PriceTypes, index: number): void {
    this.productClicked.emit({
      product,
      priceType,
      index,
    });
  }

  onProductCarouselPrev(): void {
    this.productCarouselPrev.emit();
  }

  onProductCarouselNext(): void {
    this.productCarouselNext.emit();
  }

  trackLeftSwipe(): void {
    this.swipeLeft.emit();
  }

  trackRightSwipe(): void {
    this.swipeRight.emit();
  }
}
